import React, { useEffect } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTheme } from '../context/ThemeContext';
import { api } from '../services/api';
import { normalizeReferralCode, parseReferralLinkId, recordReferralLanding } from '../utils/referralLink';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

/**
 * CORE-1156 — посадочная реферальной ссылки `/r/{code}`.
 *
 * Зачем экран, а не редирект в конфиге. Адрес `/r/{code}` фронт не знал вовсе,
 * а незнакомый путь React Navigation не отвергает — он молча показывает главную
 * и переписывает адрес на «/». Снаружи это выглядело как рабочая ссылка: человек
 * приходил на главную, регистрировался, и ничего не происходило. Переход не
 * засчитывался, `ReferralAttribution` не создавалась, привязка при регистрации
 * получала `NoAttribution`, а у реферера счётчики стояли на нуле — без единой
 * ошибки, на которую можно было бы посмотреть.
 *
 * Что делает экран: зовёт `POST /api/referral/visit`, сохраняет выданный токен
 * посетителя (он же `X-Anon-User-Id`, который клиент и так шлёт при регистрации)
 * и уводит человека на главную.
 *
 * Уводит через `reset`, а не `replace`: адрес с кодом не должен оставаться в
 * строке браузера и в истории. Иначе «назад» или перезагрузка вкладки считались
 * бы вторым переходом, и у реферера в воронке росли бы клики, которых не было.
 *
 * Экран не под `MONEY_SURFACE_ENABLED` (CORE-1157) намеренно: денежной поверхности
 * он не показывает — только спиннер и уход на главную, — а переход, пришедший на
 * телефон, засчитать так же нужно.
 */
export const ReferralLandingScreen: React.FC = () => {
    const nav = useNavigation<Nav>();
    const route = useRoute<RouteProp<RootStackParamList, 'ReferralLanding'>>();
    const { colors: C } = useTheme();
    const code = normalizeReferralCode(route.params?.code);
    // CORE-956 — `?l=` из адреса contextual-ссылки: без него переход по ссылке
    // под пост или кампанию засчитывался на общую ссылку владельца.
    const linkId = parseReferralLinkId(route.params?.l);

    useEffect(() => {
        let alive = true;
        recordReferralLanding(code, (value) => api.recordReferralVisit(value, linkId)).then(() => {
            if (alive) nav.reset({ index: 0, routes: [{ name: 'Home' }] });
        });
        return () => { alive = false; };
    }, [code, linkId, nav]);

    return (
        // CORE-1733 — ожидание переадресации — на общем фоне, без белой вспышки.
        <View style={[styles.root, { backgroundColor: C.pageBg }]}>
            <ActivityIndicator size="large" color={C.textMuted} />
        </View>
    );
};

const styles = StyleSheet.create({
    root: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
